<script lang="ts">
  import type { SanityHero, SanityPicture } from "$lib/types/shared";
  import Button from "$lib/Button.svelte";
  import SanityPictureComponent from "$lib/SanityPicture.svelte";
  import CheckmarkList from "$lib/CheckmarkList.svelte";
  import { breakpoints as br } from "$lib/utils/getBreakpoints";
  import IshavskraftLogo from "$lib/assets/IshavskraftLogo.svelte";
  import { PortableText } from "@portabletext/svelte";
  import { contentBlockComponents } from "$lib/portableText/utils";

  const sizes = `(min-width: ${br.desktop}) 40vw, (min-width: ${br.laptop}) 65vw, 100vw`;
  const linkList = [
    {
      label: "Kundeservice",
      href: "/privat/kundeservice",
      description: "Flytte? Andre spørsmål?",
      icon: "CustomerServiceIcon",
    },
    {
      label: "Spotpris",
      href: "/privat/dagens-strompris",
      description: "Dagens eller historisk spotpris",
      icon: "BarChartIcon",
    },
    {
      label: "Min side",
      href: "//app.ishavskraft.no",
      description: "Ditt forbruk og regninger",
      icon: "PersonIcon",
      rel: "nofollow",
    },
  ];

  interface Props {
    headingElement: string;
    pictureObject?: SanityPicture | null;
    hero?: SanityHero;
  }

  let { headingElement, pictureObject = null, hero }: Props = $props();
</script>

<div
  class="front-page-hero grid children-m-0 laptop:mx-edge-x laptop:rounded-2xl laptop:mb-10 mt-4 laptop:mt-5 desktop:mt-8 wide:mt-8 laptop:grid-cols-[4fr_7fr] desktop:grid-cols-[8fr_9fr] landscape:text-body relative isolate"
>
  <div
    class="self-end hyphens-manual px-edge-x laptop:pr-0 laptop:pl-0 relative z-2 max-laptop:order-1"
  >
    <svelte:element
      this={headingElement}
      class="text-3xl text-pretty tablet:text-[calc(1.25*var(--text-body)+1vw)] desktop:text-h1 my-4"
    >
      {#if hero?.header}
        <PortableText
          value={hero?.header}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      {:else}
        Velkommen til et <span class="text-$primary-strong">enkelt</span> kundeforhold
      {/if}
    </svelte:element>

    {#if hero?.content}
      <PortableText
        value={hero.content}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    {:else}
      <CheckmarkList class="my-4">
        <li>Hyggelig og personlig kundeservice</li>
        <li>Gode fordelsavtaler på strøm</li>
        <li>Full oversikt i Ishavskraft-appen</li>
        <li>Mange gode produkter for hus og hjem</li>
      </CheckmarkList>
    {/if}

    <p class="mt-8">
      <Button
        primary
        class="button-drop-shadow-lg min-h-[3rem] w-full laptop:w-initial plausible-event-name=Strømavtaler+front text-base"
        href={hero?.button?.url || "/avtale"}
      >
        {#if hero?.button?.text}
          {hero.button.text}
        {:else}
          Finn beste strømavtale hos Ishavskraft
        {/if}
      </Button>
    </p>
  </div>

  <div
    class="picture-fallback-background aspect-[16/8] laptop:aspect-[unset] relative isolate grid items-end max-laptop:mx-edge-x"
    style={pictureObject?.lqip ? `--hero-img-lqip: url("${pictureObject.lqip}")` : undefined}
  >
    {#if pictureObject?.src}
      <div class="picture-container z--1 absolute inset-0 w-full h-full">
        <SanityPictureComponent
          {pictureObject}
          {sizes}
          alt="Ishavskraft"
          class="w-full h-full object-cover object-[center_top]"
          loading="eager"
          --rounded-picture="var(--front-hero-image-rounded)"
        />
      </div>
    {/if}

    <div class="px-edge-x laptop:px-6">
      <p class="flex gap-4 items-center justify-between">
        <IshavskraftLogo --logo-color="white" class="text-h2 max-laptop:opacity-0" />
        <Button
          primary
          class="button-drop-shadow-lg min-w-0 min-h-[3rem] text-base plausible-event-name=Bli+kunde+front"
          arrow
          href="/avtale">Bli kunde</Button
        >
      </p>
    </div>
  </div>
</div>

<ul
  class="list-none p-0 laptop:my-16 mx-edge-x grid gap-y-3 gap-x-[min(1.5vw,var(--s-8))] laptop:grid-cols-3 items-center"
>
  {#each linkList as link (link)}
    <li>
      <Button href={link.href} class="text-start bg-$bg w-full" arrow>
        {#snippet iconStart()}
          {#if link.icon === "CustomerServiceIcon"}
            <svg
              aria-hidden="true"
              class="icon-support ml-[.25vw] mr-[.75vw]"
              width="39"
              height="34"
              viewBox="0 0 39 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.625 33.875V30.125H32.625V16.8125C32.625 15 32.2813 13.2969 31.5938 11.7031C30.9063 10.1094 29.9688 8.71875 28.7813 7.53125C27.5938 6.34375 26.2031 5.40625 24.6094 4.71875C23.0156 4.03125 21.3125 3.6875 19.5 3.6875C17.6875 3.6875 15.9844 4.03125 14.3906 4.71875C12.7969 5.40625 11.4062 6.34375 10.2188 7.53125C9.03125 8.71875 8.09375 10.1094 7.40625 11.7031C6.71875 13.2969 6.375 15 6.375 16.8125V28.25H4.5C3.46875 28.25 2.58625 27.8831 1.8525 27.1494C1.1175 26.4144 0.75 25.5312 0.75 24.5V20.75C0.75 20.0312 0.921875 19.3981 1.26563 18.8506C1.60938 18.3044 2.0625 17.8594 2.625 17.5156L2.76562 15.0312C3.04688 12.75 3.69562 10.6875 4.71187 8.84375C5.72687 7 6.9925 5.4375 8.50875 4.15625C10.0238 2.875 11.7269 1.8825 13.6181 1.17875C15.5081 0.47625 17.4688 0.125 19.5 0.125C21.5625 0.125 23.5394 0.47625 25.4306 1.17875C27.3206 1.8825 29.0156 2.8825 30.5156 4.17875C32.0156 5.47625 33.2738 7.03875 34.29 8.86625C35.305 10.695 35.9531 12.7344 36.2344 14.9844L36.375 17.4219C36.9375 17.7031 37.3906 18.1169 37.7344 18.6631C38.0781 19.2106 38.25 19.8125 38.25 20.4688V24.7812C38.25 25.4688 38.0781 26.0781 37.7344 26.6094C37.3906 27.1406 36.9375 27.5469 36.375 27.8281V30.125C36.375 31.1562 36.0081 32.0394 35.2744 32.7744C34.5394 33.5081 33.6562 33.875 32.625 33.875H17.625ZM13.875 20.75C13.3438 20.75 12.8988 20.57 12.54 20.21C12.18 19.8512 12 19.4062 12 18.875C12 18.3438 12.18 17.8981 12.54 17.5381C12.8988 17.1794 13.3438 17 13.875 17C14.4062 17 14.8519 17.1794 15.2119 17.5381C15.5706 17.8981 15.75 18.3438 15.75 18.875C15.75 19.4062 15.5706 19.8512 15.2119 20.21C14.8519 20.57 14.4062 20.75 13.875 20.75ZM25.125 20.75C24.5938 20.75 24.1488 20.57 23.79 20.21C23.43 19.8512 23.25 19.4062 23.25 18.875C23.25 18.3438 23.43 17.8981 23.79 17.5381C24.1488 17.1794 24.5938 17 25.125 17C25.6562 17 26.1019 17.1794 26.4619 17.5381C26.8206 17.8981 27 18.3438 27 18.875C27 19.4062 26.8206 19.8512 26.4619 20.21C26.1019 20.57 25.6562 20.75 25.125 20.75ZM8.29688 17.8438C8.17188 16 8.43 14.3281 9.07125 12.8281C9.71125 11.3281 10.57 10.0544 11.6475 9.00688C12.7262 7.96062 13.9688 7.15625 15.375 6.59375C16.7812 6.03125 18.1875 5.75 19.5938 5.75C22.4375 5.75 24.8988 6.64875 26.9775 8.44625C29.055 10.2425 30.3125 12.4844 30.75 15.1719C27.8125 15.1406 25.1562 14.3513 22.7812 12.8038C20.4062 11.2575 18.6094 9.25 17.3906 6.78125C16.8906 9.3125 15.8362 11.5544 14.2275 13.5069C12.6175 15.4606 10.6406 16.9062 8.29688 17.8438Z"
                fill="#0A66FF"
              />
            </svg>
          {:else if link.icon === "BarChartIcon"}
            <svg
              aria-hidden="true"
              class="icon-chart ml-[.25vw] mr-[.75vw]"
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M7.5 9.375H0V30H7.5V9.375Z" fill="#0A66FF" />
              <path d="M30 16.875H22.5V30H30V16.875Z" fill="#0A66FF" />
              <path d="M18.75 0H11.25V30H18.75V0Z" fill="#0A66FF" />
            </svg>
          {:else if link.icon === "PersonIcon"}
            <svg
              aria-hidden="true"
              class="icon-person ml-[.25vw] mr-[.75vw]"
              width="31"
              height="30"
              viewBox="0 0 31 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 15C13.4375 15 11.6719 14.2656 10.2031 12.7969C8.73438 11.3281 8 9.5625 8 7.5C8 5.4375 8.73438 3.67188 10.2031 2.20313C11.6719 0.734375 13.4375 0 15.5 0C17.5625 0 19.3281 0.734375 20.7969 2.20313C22.2656 3.67188 23 5.4375 23 7.5C23 9.5625 22.2656 11.3281 20.7969 12.7969C19.3281 14.2656 17.5625 15 15.5 15ZM0.5 30V24.75C0.5 23.6875 0.77375 22.7106 1.32125 21.8194C1.8675 20.9294 2.59375 20.25 3.5 19.7812C5.4375 18.8125 7.40625 18.0856 9.40625 17.6006C11.4062 17.1169 13.4375 16.875 15.5 16.875C17.5625 16.875 19.5938 17.1169 21.5938 17.6006C23.5938 18.0856 25.5625 18.8125 27.5 19.7812C28.4062 20.25 29.1325 20.9294 29.6788 21.8194C30.2262 22.7106 30.5 23.6875 30.5 24.75V30H0.5Z"
                fill="#0A66FF"
              />
            </svg>
          {/if}
        {/snippet}

        <h2 class="my-0 text-base desktop:text-body">{link.label}</h2>
        <p class="my-0 font-normal text-sm leading-tight">{link.description}</p>
      </Button>
    </li>
  {/each}
</ul>

<style lang="postcss">
  .front-page-hero {
    --front-hero-image-rounded: 1rem;
    gap: var(--s-2) var(--_gx, 0);
  }

  @media (--laptop) {
    .front-page-hero {
      --_gx: var(--s-8);
    }
  }

  .picture-fallback-background {
    border-radius: var(--front-hero-image-rounded, 1rem);
  }

  .picture-fallback-background::after {
    background: center top / cover no-repeat var(--hero-img-lqip, var(--img-fallback, transparent));
    content: "";
    display: block;
    inset: 1px; /* 1px smaller to avoid color bleed */
    position: absolute;
    z-index: -3;
  }
</style>
